/**
 * Lee packing list Excel: solo tablas con estructura fija de columnas (13+):
 * Container, Proveedor, BL, GRADO / TRATAMIENTO / TRATAMENTO, ALMACEN, NF, Paquetes, Pcs/paq, Total pcs,
 * Espesor, Ancho, Largo, Volumen [, Peso opcional].
 * Si hay varias tablas con esta estructura, se usa la última (más abajo en la hoja).
 */

import * as XLSX from 'xlsx';
import { isRwNrPackingListRow } from './packingListRwNr';
import { forwardFillContainerInDetalle } from './packingListContainer';

const norm = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

function cellText(val) {
  if (val == null || val === '') return '';
  return String(val).trim();
}

/**
 * 13 encabezados en orden obligatorio (índice 0..12).
 * Cada función recibe el texto normalizado de la celda.
 */
const HEADER_SIGNATURE_13 = [
  (h) => /^(container|contenedor)$/.test(h) || h === 'container' || h === 'contenedor',
  (h) => /^proveedor$/.test(h) || (h.includes('proveedor') && h.length < 24),
  (h) => /^bl$/.test(h) || /^bl\s*#/.test(h),
  (h) => /^(tratamiento|tratamento)$/.test(h) || h.startsWith('grado'),
  (h) => /^almacen$/.test(h) || h.includes('almacen'),
  (h) => /^nf$/.test(h) || h === 'n/f' || h === 'n.f.',
  (h) =>
    /\bpaquetes\b/.test(h) &&
    !/pcs/.test(h) &&
    !/total/.test(h),
  (h) =>
    /pcs\s*[/]\s*paq/.test(h) ||
    /piezas?\s*(por|\/)\s*paq/.test(h) ||
    /\bp\s*[/]\s*paq\b/.test(h),
  (h) => /total\s*(pcs|piezas)/.test(h) || /^total\s*pcs$/.test(h),
  (h) => /^espesor$/.test(h) || h.startsWith('espesor'),
  (h) => /^ancho$/.test(h) || h.startsWith('ancho') || /^width$/.test(h),
  (h) => /^largo$/.test(h) || h.startsWith('largo') || /^length$/.test(h),
  (h) =>
    /\bvolumen\b/.test(h) ||
    /\bm\s*3\b/.test(h) ||
    /m³/.test(h) ||
    /^vol\.?$/.test(h),
];

function matchesSignature13(sliceNorm) {
  if (!sliceNorm || sliceNorm.length < 13) return false;
  for (let i = 0; i < 13; i++) {
    const h = sliceNorm[i];
    if (!h) return false;
    if (!HEADER_SIGNATURE_13[i](h)) return false;
  }
  return true;
}

function rowToNormalizedCells(row) {
  return (row || []).map((c) => norm(cellText(c)));
}

/**
 * Busca en la fila una ventana de 13 celdas consecutivas que coincida con la firma.
 * @returns {{ startCol: number } | null}
 */
function findSignatureStartInRow(row) {
  const cells = rowToNormalizedCells(row);
  const maxStart = cells.length - 13;
  if (maxStart < 0) return null;
  for (let start = 0; start <= maxStart; start++) {
    const slice = cells.slice(start, start + 13);
    if (matchesSignature13(slice)) {
      return { startCol: start };
    }
  }
  return null;
}

function isPesoHeader(h) {
  if (!h) return false;
  return /^peso$/.test(h) || (h.startsWith('peso') && !/proveedor/.test(h));
}

/** Columnas opcionales de precio PTB (después de Volumen / Peso en el Excel comercial). */
function detectOptionalPriceColumns(headerRow, startCol) {
  const cells = rowToNormalizedCells(headerRow);
  const map = {};
  for (let i = startCol + 13; i < cells.length; i++) {
    const h = cells[i];
    if (!h) continue;
    if (
      /proveedor.*usd.*m/.test(h) ||
      (/usd/.test(h) && /m\s*3|m3|m³/.test(h) && !/total/.test(h))
    ) {
      map.precio_usd_m3 = i;
      continue;
    }
    if (/^total\s*usd/.test(h) || (/\btotal\b/.test(h) && /\busd\b/.test(h) && !/m\s*3|m3/.test(h))) {
      map.total_usd = i;
    }
  }
  return map;
}

function forwardFillByContainer(rows, field) {
  const parseField =
    field === 'precio_usd_m3' || field === 'total_usd' ? parseExcelMoneyNumber : parseExcelNumber;
  let last = null;
  let lastCont = null;
  return rows.map((r) => {
    const c = containerKey(r);
    const n = parseField(r[field]);
    if (!c) {
      last = null;
      lastCont = null;
      return { ...r, [field]: n != null ? n : null };
    }
    if (c !== lastCont) {
      lastCont = c;
      last = null;
    }
    if (n != null) last = n;
    return { ...r, [field]: n != null ? n : last };
  });
}

/**
 * @param {number} startCol
 * @param {Array} headerRow
 */
function buildColMapFromSignature(startCol, headerRow) {
  const cells = rowToNormalizedCells(headerRow);
  const m = {
    container: startCol + 0,
    proveedor: startCol + 1,
    bl: startCol + 2,
    grado: startCol + 3,
    almacen: startCol + 4,
    nf: startCol + 5,
    paquetes: startCol + 6,
    pcs_por_paq: startCol + 7,
    total_pcs: startCol + 8,
    espesor: startCol + 9,
    ancho: startCol + 10,
    largo: startCol + 11,
    volumen_m3: startCol + 12,
  };
  const pesoIdx = startCol + 13;
  if (cells.length > pesoIdx && isPesoHeader(cells[pesoIdx])) {
    m.peso = pesoIdx;
  }
  return m;
}

/**
 * Encuentra todas las filas que contienen la firma completa (cualquier offset horizontal).
 * @returns {Array<{ rowIndex: number, startCol: number }>}
 */
function findAllTableHeaders(rows) {
  const found = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row.length) continue;
    const hit = findSignatureStartInRow(row);
    if (hit) {
      found.push({ rowIndex: i, startCol: hit.startCol });
    }
  }
  return found;
}

/** Convierte valor de celda a número (coma decimal europea). */
export function parseExcelNumber(val) {
  if (val == null || val === '') return null;
  if (typeof val === 'number' && !Number.isNaN(val)) return val;
  let s = String(val).trim().replace(/\s/g, '');
  if (s === '') return null;

  const hasComma = s.includes(',');
  const hasDot = s.includes('.');

  if (hasComma && hasDot) {
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma > lastDot) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
  } else if (hasComma) {
    const parts = s.split(',');
    if (parts.length === 2 && parts[1].length <= 3) {
      s = `${parts[0].replace(/\./g, '')}.${parts[1]}`;
    } else {
      s = s.replace(/,/g, '');
    }
  }

  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Montos USD del Excel (Total usd, Proveedor usd/m³).
 * Soporta 3.840,27 · 3,840.27 · 3.840 (miles) · 205,00.
 */
export function parseExcelMoneyNumber(val) {
  if (val == null || val === '') return null;
  if (typeof val === 'number' && !Number.isNaN(val)) return val;
  let s = String(val).trim().replace(/\s/g, '');
  if (s === '') return null;

  const hasComma = s.includes(',');
  const hasDot = s.includes('.');

  if (hasComma && hasDot) {
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma > lastDot) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      s = s.replace(/,/g, '');
    }
  } else if (hasComma) {
    const parts = s.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      s = `${parts[0].replace(/\./g, '')}.${parts[1]}`;
    } else {
      s = s.replace(/,/g, '');
    }
  } else if (hasDot) {
    const parts = s.split('.');
    if (parts.length === 2 && parts[1].length === 3 && parts[0].length <= 1) {
      // 3.840 → 3840 (miles sin decimales; no aplica a 18.733 en columnas m³)
      s = parts[0] + parts[1];
    } else if (parts.length > 2) {
      s = s.replace(/\./g, '');
    }
  }

  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/** M³ en pantalla: coma decimal (es-CL), hasta 3 decimales. */
export function formatM3Display(val) {
  if (val == null || val === '') return '';
  const n = typeof val === 'number' ? val : parseExcelNumber(val);
  if (n == null || Number.isNaN(n)) return '';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(n);
}

function parseIntegerLike(val) {
  if (val == null || val === '') return null;
  if (typeof val === 'number' && Number.isFinite(val)) return Math.round(val);
  const s = String(val).trim().replace(/\s/g, '');
  if (!s) return null;
  const noThousands = s.replace(/,/g, '');
  if (/^-?\d+(\.\d+)?$/.test(noThousands)) {
    const n = Number.parseFloat(noThousands);
    return Number.isFinite(n) ? Math.round(n) : null;
  }
  const fallback = parseExcelNumber(val);
  return Number.isFinite(fallback) ? Math.round(fallback) : null;
}

function normalizeTotalPcsByRule(rec) {
  if (isRwNrPackingListRow(rec)) return { ok: true, row: rec };

  const paquetes = parseExcelNumber(rec.cantidad_paquetes);
  const pcsPaq = parseIntegerLike(rec.piezas_por_paquete);
  if (!Number.isFinite(paquetes) || !Number.isFinite(pcsPaq)) return { ok: true, row: rec };

  const esperado = Math.round(paquetes * pcsPaq);
  const informado = Number.isFinite(rec.total_piezas) ? Math.round(rec.total_piezas) : null;
  if (informado == null) return { ok: true, row: { ...rec, total_piezas: esperado } };
  if (informado === esperado) return { ok: true, row: rec };

  // Caso frecuente en Excel: "2,030" leído como 2.03 por parse decimal.
  const totalRawMiles = parseIntegerLike(rec._total_piezas_raw);
  if (Number.isFinite(totalRawMiles) && totalRawMiles === esperado) {
    return { ok: true, row: { ...rec, total_piezas: totalRawMiles } };
  }
  return {
    ok: false,
    esperado,
    informado,
    fila: rec._filaExcel,
    paquetes,
    pcsPaq,
  };
}

/**
 * Fila sin datos en las columnas de detalle de la tabla (núcleo del packing).
 * Si ya había filas leídas, la primera fila así después de los datos marca el fin de la tabla.
 */
function isRowEmptyForData(row, colMap) {
  const keys = ['paquetes', 'pcs_por_paq', 'total_pcs', 'espesor', 'ancho', 'largo', 'volumen_m3'];
  const coreEmpty = keys.every((k) => {
    const idx = colMap[k];
    if (idx == null) return true;
    const v = row[idx];
    return v == null || String(v).trim() === '';
  });
  if (!coreEmpty) return false;
  if (colMap.peso != null) {
    const v = row[colMap.peso];
    if (v != null && String(v).trim() !== '') return false;
  }
  return true;
}

/** Clave de contenedor para agrupar filas (misma columna en Excel = mismo peso total). */
function containerKey(r) {
  return String(r?.container_celda ?? r?._container_excel ?? '')
    .trim();
}

/**
 * Rellena peso bruto hacia abajo solo dentro del mismo contenedor, como en el Excel
 * (un solo Peso para varias filas del mismo container).
 */
function forwardFillPesoByContainer(rows, colMap) {
  if (colMap.peso == null) return rows;
  let last = null;
  let lastCont = null;
  return rows.map((r) => {
    const c = containerKey(r);
    const n = parseExcelNumber(r.peso);
    if (!c) {
      last = null;
      lastCont = null;
      return { ...r, peso: n != null ? n : null };
    }
    if (c !== lastCont) {
      lastCont = c;
      last = null;
    }
    if (n != null) last = n;
    return { ...r, peso: n != null ? n : last };
  });
}

/**
 * @param {ArrayBuffer} arrayBuffer
 */
export function parsePackingListExcel(arrayBuffer) {
  let workbook;
  try {
    workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
  } catch (e) {
    return { ok: false, error: 'No se pudo leer el archivo Excel.' };
  }
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) {
    return { ok: false, error: 'La hoja está vacía.' };
  }

  // raw: true conserva decimales en precios (ej. 198,2 mostrado como «198» con formato Excel).
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true });
  if (!rows || rows.length === 0) {
    return { ok: false, error: 'No hay datos en la hoja.' };
  }

  const headers = findAllTableHeaders(rows);
  if (headers.length === 0) {
    return {
      ok: false,
      error:
        'No se encontró una tabla con la estructura requerida: ' +
        'Container, Proveedor, BL, GRADO / TRATAMIENTO / TRATAMENTO, ALMACEN, NF, Paquetes, Pcs/paq, Total pcs, ' +
        'Espesor, Ancho, Largo, Volumen (en ese orden).',
    };
  }

  // Preferir tabla con columnas de precio PTB; si ninguna, la última (comportamiento previo).
  let chosen = headers[headers.length - 1];
  for (let i = headers.length - 1; i >= 0; i--) {
    const cand = headers[i];
    const priceMap = detectOptionalPriceColumns(rows[cand.rowIndex], cand.startCol);
    if (priceMap.precio_usd_m3 != null || priceMap.total_usd != null) {
      chosen = cand;
      break;
    }
  }
  const { rowIndex: headerRowIndex, startCol } = chosen;
  const headerRow = rows[headerRowIndex];
  const colMap = buildColMapFromSignature(startCol, headerRow);
  const priceColMap = detectOptionalPriceColumns(headerRow, startCol);
  Object.assign(colMap, priceColMap);

  const colLetter = (idx) =>
    typeof idx === 'number' && idx >= 0 ? XLSX.utils.encode_col(idx) : '';

  const columnLetters = {};
  Object.keys(colMap).forEach((k) => {
    columnLetters[k] = colLetter(colMap[k]);
  });

  const dataRows = [];
  for (let r = headerRowIndex + 1; r < rows.length; r++) {
    const raw = rows[r] || [];
    if (isRowEmptyForData(raw, colMap)) {
      /* Fin de bloque de datos: no seguir leyendo el resto de la hoja (notas, totales, otras tablas). */
      if (dataRows.length > 0) break;
      continue;
    }

    const gradoT = cellText(raw[colMap.grado]);
    const provT = cellText(raw[colMap.proveedor]);
    const almT = cellText(raw[colMap.almacen]);
    const containerExcelText = cellText(raw[colMap.container]);
    const rec = {
      _filaExcel: r + 1,
      _container: parseExcelNumber(raw[colMap.container]),
      /** Texto crudo de la columna Container (útil para contar contenedores distintos y PDF). */
      _container_excel: containerExcelText,
      /** Misma vista que leerás en "Container" al cargar; editable en la grilla. */
      container_celda: containerExcelText,
      proveedor_celda: provT,
      bl_celda: cellText(raw[colMap.bl]),
      grado_celda: gradoT,
      almacen_celda: almT,
      /** Products se rellena en la UI: categoría (OC) o edición manual; el PDF puede usar grado/proveedor/almacen si no hay texto. */
      nf_celda: cellText(raw[colMap.nf]),
      cantidad_paquetes: parseExcelNumber(raw[colMap.paquetes]),
      piezas_por_paquete: cellText(raw[colMap.pcs_por_paq]),
      _total_piezas_raw: raw[colMap.total_pcs],
      total_piezas: parseExcelNumber(raw[colMap.total_pcs]),
      espesor: parseExcelNumber(raw[colMap.espesor]),
      ancho: cellText(raw[colMap.ancho]),
      largo: parseExcelNumber(raw[colMap.largo]),
      total_m3: parseExcelNumber(raw[colMap.volumen_m3]),
      peso: colMap.peso != null ? raw[colMap.peso] : undefined,
      precio_usd_m3:
        colMap.precio_usd_m3 != null ? parseExcelMoneyNumber(raw[colMap.precio_usd_m3]) : undefined,
      total_usd: colMap.total_usd != null ? parseExcelMoneyNumber(raw[colMap.total_usd]) : undefined,
    };

    const hasAny =
      rec.cantidad_paquetes != null ||
      rec.total_piezas != null ||
      rec.espesor != null ||
      rec.ancho ||
      rec.largo != null ||
      rec.total_m3 != null ||
      (rec.peso != null && String(rec.peso).trim() !== '') ||
      rec.proveedor_celda ||
      rec.bl_celda ||
      rec.nf_celda ||
      containerExcelText;
    if (!hasAny) continue;

    dataRows.push(rec);
  }

  if (dataRows.length === 0) {
    return {
      ok: false,
      error: 'La tabla detectada no tiene filas de datos debajo del encabezado.',
    };
  }

  const filledPeso = forwardFillPesoByContainer(dataRows, colMap);
  let filled = filledPeso;
  if (colMap.precio_usd_m3 != null) {
    filled = forwardFillByContainer(filled, 'precio_usd_m3');
  }
  if (colMap.total_usd != null) {
    // No rellenar total hacia abajo: cada fila tiene su monto; solo normalizar número.
    filled = filled.map((r) => ({
      ...r,
      total_usd: r.total_usd != null ? parseExcelMoneyNumber(r.total_usd) : null,
    }));
  }
  const validated = [];
  const mismatches = [];
  filled.forEach((row) => {
    const out = {
      ...row,
      peso_neto: parseExcelNumber(row.peso),
    };
    const v = normalizeTotalPcsByRule(out);
    if (!v.ok) {
      mismatches.push(v);
      return;
    }
    validated.push(v.row);
  });

  if (mismatches.length > 0) {
    const detalle = mismatches
      .slice(0, 5)
      .map(
        (m) =>
          `fila ${m.fila}: paquetes (${m.paquetes}) × pcs/paq (${m.pcsPaq}) = ${m.esperado}, pero Total pcs = ${m.informado}`
      )
      .join('; ');
    const extra = mismatches.length > 5 ? ` (+${mismatches.length - 5} filas más)` : '';
    return {
      ok: false,
      error:
        `Error al cargar Excel: revise "Total pcs". Debe coincidir con paquetes × pcs/paq. ` +
        `${detalle}${extra}.`,
    };
  }

  let sumPaquetes = 0;
  let sumM3 = 0;
  validated.forEach((row) => {
    if (row.cantidad_paquetes != null) sumPaquetes += row.cantidad_paquetes;
    if (row.total_m3 != null) sumM3 += row.total_m3;
  });

  const contVals = validated
    .map((row) => row._container_excel)
    .filter((v) => v != null && String(v).trim() !== '');
  const contenedoresEstimado =
    contVals.length > 0 ? new Set(contVals.map((v) => String(v).trim())).size : null;

  return {
    ok: true,
    headerRowIndex,
    startCol,
    columnLetters,
    rows: validated,
    totalPaquetes: sumPaquetes,
    totalVolumenM3: sumM3,
    contenedoresEstimado,
  };
}
